import React, { useState, useEffect } from 'react';
import { Switch } from '@headlessui/react';
import LogoPaypal from 'assets/logo-paypal.png';
import { PayPalDialog } from './PayPalDialog';
import { PayPalAPI } from 'api/paypal';
import useAuthState from '../global-states/useAuthState';
import toast from 'react-hot-toast';
import db from '../firebase-logic/firebase';
import { useTranslation } from 'react-i18next';
import { PayPalDisconnectDialog } from './PayPalDisconnectDialog';
import { useHistory, useLocation } from 'react-router-dom';
import LoadingScreen from './LoadingScreen';
import { REACT_APP_PAYPAL_API_KEY } from 'config';

const PAYPAL_CALLBACK_PARAMETERS = [
  'paypal',
  'merchantId',
  'trackingId',
  'merchantIdInPayPal',
  'permissionsGranted',
  'accountStatus',
  'consentStatus',
  'productIntentId',
  'isEmailConfirmed',
  'returnMessage',
  'riskStatus',
];

const isObject = (value: unknown): value is Record<string, any> =>
  typeof value === 'object' && value !== null;

const getPayPalConnectionError = (
  accountInformation: unknown,
  expectedPartnerClientId: string
) => {
  if (!isObject(accountInformation)) return undefined;

  if (accountInformation.payments_receivable !== true) {
    return 'paypal.error.payments_receivable';
  }

  if (accountInformation.primary_email_confirmed !== true) {
    return 'paypal.error.primary_email_confirmed';
  }

  const hasDroovOAuthIntegration =
    accountInformation.has_droov_oauth_integration === true ||
    (expectedPartnerClientId.length > 0 &&
      Array.isArray(accountInformation.oauth_integrations) &&
      accountInformation.oauth_integrations.some(
        (integration: unknown) =>
          isObject(integration) &&
          integration.integration_type === 'OAUTH_THIRD_PARTY' &&
          integration.integration_method === 'PAYPAL' &&
          Array.isArray(integration.oauth_third_party) &&
          integration.oauth_third_party.some(
            (thirdParty: unknown) =>
              isObject(thirdParty) &&
              thirdParty.partner_client_id === expectedPartnerClientId
          )
      ));

  return hasDroovOAuthIntegration
    ? undefined
    : 'paypal.error.oauth_third_party';
};

const isPayPalRedirectUrl = (value: unknown) => {
  if (typeof value !== 'string') return false;

  try {
    const url = new URL(value);
    return (
      url.protocol === 'https:' &&
      (url.hostname === 'paypal.com' || url.hostname.endsWith('.paypal.com'))
    );
  } catch {
    return false;
  }
};

export const PaypalPaymentSetting: React.FC = () => {
  const [active, setActive] = useState(false);
  const [connected, setConnected] = useState(false);
  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const [disconnectDialogIsOpen, setDisconnectDialogIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const history = useHistory();
  const location = useLocation();
  const auth: any = useAuthState.getState();

  const { t } = useTranslation(['settings']);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      setLoading(true);
      setIsLoading(true);

      const callbackParameters = new URLSearchParams(location.search);
      const legacyTrackingId = callbackParameters.get('merchantId');
      const explicitTrackingId = callbackParameters.get('trackingId');
      const trackingId = explicitTrackingId || legacyTrackingId;
      const merchantIdInPayPal = callbackParameters.get('merchantIdInPayPal');
      const hasPayPalCallback = PAYPAL_CALLBACK_PARAMETERS.some((parameter) =>
        callbackParameters.has(parameter)
      );

      const applyAccountInformation = (accountInformation: unknown) => {
        const connectionError = getPayPalConnectionError(
          accountInformation,
          REACT_APP_PAYPAL_API_KEY(auth.user)
        );

        if (isMounted) {
          setConnected(isObject(accountInformation) && !connectionError);
          if (connectionError) toast.error(t(connectionError));
        }
      };

      try {
        const companyDocument = await db
          .collection('companies')
          .doc(auth.user)
          .get();
        const companyData = companyDocument.data();
        const paymentSettings = companyData?.paymentSettings || {};

        if (isMounted) setActive(Boolean(paymentSettings.payPalPayment));

        if (hasPayPalCallback) {
          const returnedTrackingIds = [
            legacyTrackingId,
            explicitTrackingId,
          ].filter((value): value is string => value !== null);
          const callbackMatchesAuthenticatedCompany =
            trackingId !== null &&
            returnedTrackingIds.every((value) => value === auth.user);

          if (!callbackMatchesAuthenticatedCompany || !merchantIdInPayPal) {
            throw new Error('Invalid PayPal onboarding callback');
          }

          const accountInformation = await PayPalAPI.completeOnboarding(
            auth.user,
            trackingId,
            merchantIdInPayPal
          );
          if (!isObject(accountInformation)) {
            throw new Error('PayPal onboarding verification failed');
          }
          applyAccountInformation(accountInformation);
        } else if (paymentSettings.payPal) {
          const accountInformation = await PayPalAPI.getAccountInformation(
            auth.user
          );
          applyAccountInformation(accountInformation);
        } else if (isMounted) {
          setConnected(false);
        }
      } catch (error) {
        console.error('Unable to load PayPal payment settings');
        if (isMounted) {
          setConnected(false);
          if (hasPayPalCallback) {
            toast.error(t('redirection_to_onboarding.error'));
          }
        }
      } finally {
        if (hasPayPalCallback) {
          PAYPAL_CALLBACK_PARAMETERS.forEach((parameter) =>
            callbackParameters.delete(parameter)
          );
          const cleanedSearch = callbackParameters.toString();
          history.replace({
            pathname: location.pathname,
            search: cleanedSearch ? `?${cleanedSearch}` : '',
          });
        }

        if (isMounted) {
          setIsLoading(false);
          setLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [auth.user, history, location.pathname, location.search, t]);

  if (loading) {
    return <LoadingScreen />;
  }

  const createPartnerReferral = async (companyId: string) => {
    setIsLoading(true);
    try {
      const partnerReferral = await PayPalAPI.createPartnerReferral(companyId);
      if (!partnerReferral.ok) {
        toast.error(t('redirection_to_onboarding.error'));
        return;
      }

      const partnerReferralData = await partnerReferral.json();
      if (!isPayPalRedirectUrl(partnerReferralData.href)) {
        throw new Error('PayPal returned an invalid onboarding URL');
      }

      toast.success(t('redirection_to_onboarding'));
      window.location.assign(partnerReferralData.href);
    } catch (error) {
      console.error('Unable to start PayPal onboarding');
      toast.error(t('redirection_to_onboarding.error'));
    } finally {
      setIsLoading(false);
    }
  };
  const toggleSwitch = () => {
    if (connected || active) {
      db.collection('companies')
        .doc(auth.user)
        .update({ 'paymentSettings.payPalPayment': !active });
      toast.success(
        !active
          ? t('paypalactivation.success')
          : t('paypaldeactivation.success')
      );
      setActive(!active);
    } else {
      toast.error(t('paypalactivation.failure'));
    }
  };

  return (
    <div className='w-full bg-white p-4 rounded-md max-w-3xl mx-auto mt-8 cursor-pointer'>
      <PayPalDisconnectDialog
        disconnectDialogIsOpen={disconnectDialogIsOpen}
        onClose={() => setDisconnectDialogIsOpen(false)}
        onDisconnectAccount={async () => {
          setIsLoading(true);
          try {
            const docFromPath = await db
              .collection('companies')
              .doc(auth.user)
              .get();
            const doc = docFromPath.data();

            if (doc != null) {
              db.collection('companies').doc(auth.user).update({
                'paymentSettings.payPal': null,
              });
            }
            setConnected(false);
          } catch (error) {
            console.error(error);
          }
          db.collection('companies')
            .doc(auth.user)
            .update({ 'paymentSettings.payPalPayment': false });
          setActive(false);
          setIsLoading(false);
          setDisconnectDialogIsOpen(false);
        }}
      />
      <PayPalDialog
        dialogIsOpen={dialogIsOpen}
        onClose={() => setDialogIsOpen(false)}
        isLoading={isLoading}
        onCreateAccountLink={() => createPartnerReferral(auth.user)}
        hasCompletedOnboarding={connected}
      />
      <div className='w-full flex justify-between'>
        <h2 className='font-medium text-lg'>
          {t('acceptpaypalpayments.labels')}
        </h2>
        {isLoading ? (
          <div className='loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-6 w-6'></div>
        ) : (
          <Switch
            checked={active}
            onChange={toggleSwitch}
            className={`${
              active ? 'bg-droov' : 'bg-gray-200'
            } relative inline-flex items-center h-6 rounded-full w-11`}
          >
            <span
              className={`${
                active ? 'translate-x-6' : 'translate-x-1'
              } inline-block w-4 h-4 transform bg-white rounded-full`}
            />
          </Switch>
        )}
      </div>
      <p className='droov_text_description_light mt-1'>
        {t('acceptpaypalpayments.text')}
      </p>
      <div className='flex justify-start gap-8 items-center my-4'>
        <img src={LogoPaypal} alt='PayPal' className='h-8' />
      </div>
      <div className='flex justify-between items-center'>
        {isLoading ? (
          <label className='italic text-base text-gray-400'>
            &#8226; {t('loading.label')}
          </label>
        ) : (
          <label
            className={`italic text-base ${
              connected ? 'text-green-400' : 'text-red-400'
            }`}
          >
            &#8226; {connected ? t('connected.label') : t('notconnected.label')}
          </label>
        )}
        {connected ? (
          <button
            className='relative_add_button h-10'
            onClick={() => {
              setDisconnectDialogIsOpen(true);
            }}
          >
            {t('disconnectpaypal.button')}
          </button>
        ) : (
          <button
            className='relative_add_button h-10'
            onClick={() => setDialogIsOpen(true)}
          >
            {t('connectpaypal.button')}
          </button>
        )}
      </div>
    </div>
  );
};
